import { http, HttpResponse } from 'msw';
import { fakeUsersList, fakeCreateUserPayload } from './faker';
import { ListUsersManagerialResponse } from '../api/user/users.types';
import { User } from '../types';

// -------------------------------------------------------
// Store interno com shape completo (User)
// -------------------------------------------------------
let usersStore: User[] = fakeUsersList(20).map((u) => ({
  ...fakeCreateUserPayload(),
  ...u,
}));

// -------------------------------------------------------
// Helpers
// -------------------------------------------------------
const toListResponse = (user: User): ListUsersManagerialResponse => ({
  id: user.id!,
  name: user.name,
  email: user.email,
  role: user.role,
});

const applyFilters = (users: User[], params: URLSearchParams): User[] => {
  let result = [...users];

  const name = params.get('name');
  const email = params.get('email');
  const role = params.get('role');
  const sortBy = params.get('sortBy') as keyof User | null;
  const sortOrder = params.get('sortOrder') ?? 'asc';

  if (name)
    result = result.filter((u) =>
      u.name.toLowerCase().includes(name.toLowerCase()),
    );
  if (email)
    result = result.filter((u) =>
      u.email.toLowerCase().includes(email.toLowerCase()),
    );
  if (role) result = result.filter((u) => u.role === role);

  if (sortBy) {
    result.sort((a, b) => {
      const valA = String(a[sortBy] ?? '');
      const valB = String(b[sortBy] ?? '');
      return sortOrder === 'desc'
        ? valB.localeCompare(valA)
        : valA.localeCompare(valB);
    });
  }

  return result;
};

const paginate = <T>(items: T[], page: number, pageSize: number) => ({
  data: items.slice((page - 1) * pageSize, page * pageSize),
  total: items.length,
  page,
  pageSize,
  totalPages: Math.ceil(items.length / pageSize),
});

// -------------------------------------------------------
// Handlers
// -------------------------------------------------------

const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
const BASE = `${API_URL}/v1/managerial/users`;

export const userHandlers = [

  // GET /v1/managerial/users — listagem paginada com filtros
  http.get(`${BASE}`, ({ request }) => {
    const params = new URL(request.url).searchParams;
    const page = Number(params.get('page') ?? 1);
    const pageSize = Number(params.get('pageSize') ?? 10);

    const filtered = applyFilters(usersStore, params);
    const paged = paginate(filtered.map(toListResponse), page, pageSize);

    return HttpResponse.json(paged, { status: 200 });
  }),

  // GET /v1/managerial/users/:id — usuário completo (User)
  http.get(`${BASE}/:id`, ({ params }) => {
    const { id } = params;
    const user = usersStore.find((u) => u.id === id);

    if (!user) {
      return HttpResponse.json(
        { message: 'Usuário não encontrado' },
        { status: 404 },
      );
    }

    return HttpResponse.json(user, { status: 200 });
  }),

  // POST /v1/managerial/users — cria usuário, retorna { id }
  http.post(BASE, async ({ request }) => {
    const body = await request.json() as Omit<User, 'id'>;
    const newUser: User = { ...body, id: crypto.randomUUID() };
    usersStore.push(newUser);

    return HttpResponse.json({ id: newUser.id }, { status: 201 });
  }),

  // PUT /v1/managerial/users/:id — edita usuário, retorna User completo
  http.put(`${BASE}/:id`, async ({ request, params }) => {
    const { id } = params;
    const body = await request.json() as Omit<User, 'id'>;
    const index = usersStore.findIndex((u) => u.id === id);

    if (index === -1) {
      return HttpResponse.json(
        { message: 'Usuário não encontrado' },
        { status: 404 },
      );
    }

    usersStore[index] = { ...usersStore[index], ...body };

    return HttpResponse.json(usersStore[index], { status: 200 });
  }),

  // DELETE /v1/managerial/users/:id — remove usuário, sem body
  http.delete(`${BASE}/:id`, ({ params }) => {
    const { id } = params;
    const exists = usersStore.some((u) => u.id === id);

    if (!exists) {
      return HttpResponse.json(
        { message: 'Usuário não encontrado' },
        { status: 404 },
      );
    }

    usersStore = usersStore.filter((u) => u.id !== id);

    return new HttpResponse(null, { status: 204 });
  }),
];